import React, { useEffect, useState, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import {
  AdminModal,
  AdminInput,
  AdminSelect,
  AdminTextarea,
  StatusBadge,
  formatDate,
  toDateInput,
} from '../../components/admin/AdminUI';
import { Plus, Pencil, X } from 'lucide-react';

const SITE_TYPES = ['RV Sites', 'Primitive Campsite'];
const STATUSES = ['confirmed', 'pending', 'cancelled'];
const PAYMENT_STATUSES = ['paid', 'pending', 'failed'];

const emptyForm = () => ({
  site_type: 'RV Sites',
  check_in: '',
  check_out: '',
  guests: 2,
  camping_type: 'tent',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  total_amount: '',
  status: 'confirmed',
  payment_status: 'paid',
  notes: '',
});

export const AdminBookings = () => {
  const { authedAxios } = useAdminAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null); // {mode:'add'|'edit', booking?}
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get('/api/admin/bookings');
      setBookings(data.bookings || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm(emptyForm());
    setModal({ mode: 'add' });
  };

  const openEdit = (b) => {
    setForm({
      ...emptyForm(),
      ...b,
      check_in: toDateInput(b.check_in),
      check_out: toDateInput(b.check_out),
      total_amount: b.total_amount ?? '',
      notes: b.notes || '',
    });
    setModal({ mode: 'edit', booking: b });
  };

  const close = () => {
    setModal(null);
    setForm(emptyForm());
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        check_in: form.check_in ? new Date(form.check_in).toISOString() : '',
        check_out: form.check_out ? new Date(form.check_out).toISOString() : '',
        total_amount: form.total_amount === '' ? null : parseFloat(form.total_amount),
        guests: parseInt(form.guests) || 1,
      };
      if (modal.mode === 'add') {
        await authedAxios().post('/api/admin/bookings', payload);
      } else {
        await authedAxios().patch(`/api/admin/bookings/${modal.booking.id}`, payload);
      }
      close();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (b) => {
    if (!window.confirm(`Cancel booking for ${b.first_name} ${b.last_name}?`)) return;
    try {
      await authedAxios().delete(`/api/admin/bookings/${b.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const onField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (loading) return <p className="text-[#F4F1EA]/60">Loading bookings...</p>;
  if (err) return <p className="text-red-400">{err}</p>;

  return (
    <div data-testid="admin-bookings-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-[#F4F1EA]">Bookings</h2>
          <p className="text-[#F4F1EA]/60 mt-1">{bookings.length} total</p>
        </div>
        <Button
          data-testid="admin-add-booking-btn"
          onClick={openAdd}
          className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Booking
        </Button>
      </div>

      <div className="overflow-x-auto bg-[#0B132B] border border-[#B8860B]/30 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-[#050814] text-[#B8860B] uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left p-3">Guest</th>
              <th className="text-left p-3">Site</th>
              <th className="text-left p-3">Dates</th>
              <th className="text-left p-3">Total</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Payment</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 && (
              <tr>
                <td colSpan="7" className="p-6 text-center text-[#F4F1EA]/60">
                  No bookings yet.
                </td>
              </tr>
            )}
            {bookings.map((b) => (
              <tr
                key={b.id}
                data-testid={`admin-booking-row-${b.id}`}
                className="border-t border-[#B8860B]/10 hover:bg-[#050814]/60"
              >
                <td className="p-3">
                  <p className="font-semibold text-[#F4F1EA]">
                    {b.first_name} {b.last_name}
                  </p>
                  <p className="text-xs text-[#F4F1EA]/60">{b.email}</p>
                  <p className="text-xs text-[#F4F1EA]/60">{b.phone}</p>
                </td>
                <td className="p-3 text-[#F4F1EA]/85">
                  <div className="flex items-center gap-2">
                    <span>{b.site_type}</span>
                    {b.stay_type && b.stay_type !== 'nightly' && (
                      <span
                        data-testid={`admin-stay-type-${b.id}`}
                        className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#B8860B]/15 text-[#E5C158] border border-[#B8860B]/40"
                      >
                        {b.stay_type}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#F4F1EA]/60">
                    {b.guests} guest{b.guests === 1 ? '' : 's'} • {b.nights} nights
                  </p>
                </td>
                <td className="p-3 text-[#F4F1EA]/85">
                  <p>{formatDate(b.check_in)}</p>
                  <p className="text-xs text-[#F4F1EA]/60">→ {formatDate(b.check_out)}</p>
                </td>
                <td className="p-3 text-[#B8860B] font-semibold">
                  ${(b.total_amount || 0).toFixed(2)}
                </td>
                <td className="p-3">
                  <StatusBadge status={b.status} />
                </td>
                <td className="p-3">
                  <StatusBadge status={b.payment_status} />
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button
                    data-testid={`admin-edit-booking-${b.id}`}
                    onClick={() => openEdit(b)}
                    className="inline-flex items-center px-2 py-1 text-[#B8860B] hover:bg-[#B8860B]/15 rounded mr-1"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  {b.status !== 'cancelled' && (
                    <button
                      data-testid={`admin-cancel-booking-${b.id}`}
                      onClick={() => cancel(b)}
                      className="inline-flex items-center px-2 py-1 text-red-400 hover:bg-red-500/15 rounded"
                      title="Cancel"
                    >
                      <X size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AdminModal
        testid="admin-booking-modal"
        open={!!modal}
        onClose={close}
        title={modal?.mode === 'add' ? 'Add Booking' : 'Edit Booking'}
        footer={
          <>
            <Button variant="outline" onClick={close} className="border-[#B8860B]/40 text-[#F4F1EA]">
              Cancel
            </Button>
            <Button
              data-testid="admin-booking-save-btn"
              onClick={save}
              disabled={saving}
              className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <AdminSelect label="Site Type" value={form.site_type} onChange={onField('site_type')}>
            {SITE_TYPES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </AdminSelect>
          <AdminInput label="Total ($)" type="number" step="0.01" value={form.total_amount} onChange={onField('total_amount')} />
          <AdminInput label="Check-in" type="date" value={form.check_in} onChange={onField('check_in')} required />
          <AdminInput label="Check-out" type="date" value={form.check_out} onChange={onField('check_out')} required />
          <AdminInput label="First Name" value={form.first_name} onChange={onField('first_name')} required />
          <AdminInput label="Last Name" value={form.last_name} onChange={onField('last_name')} required />
          <AdminInput label="Email" type="email" value={form.email} onChange={onField('email')} required />
          <AdminInput label="Phone" value={form.phone} onChange={onField('phone')} required />
          <AdminInput label="Guests" type="number" min="1" value={form.guests} onChange={onField('guests')} />
          <AdminSelect label="Camping Type" value={form.camping_type} onChange={onField('camping_type')}>
            <option value="tent">Tent</option>
            <option value="rv">RV / Camper</option>
            <option value="other">Other</option>
          </AdminSelect>
          <AdminSelect label="Status" value={form.status} onChange={onField('status')}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </AdminSelect>
          <AdminSelect label="Payment" value={form.payment_status} onChange={onField('payment_status')}>
            {PAYMENT_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </AdminSelect>
        </div>
        <AdminTextarea label="Internal Notes" value={form.notes} onChange={onField('notes')} />
      </AdminModal>
    </div>
  );
};

export default AdminBookings;
